import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

export type SectionLink = { id: string; label: string }

function useActiveSection(ids: string[]) {
  const [active, setActive] = useState<string | null>(null)
  const key = ids.join('|')

  useEffect(() => {
    if (typeof window.IntersectionObserver !== 'function') return
    const elements = key
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      { rootMargin: '-25% 0px -65% 0px' },
    )
    for (const element of elements) observer.observe(element)
    return () => observer.disconnect()
  }, [key])

  return active
}

export function SectionNav({
  sections,
  label = 'On this page',
  className,
}: {
  sections: SectionLink[]
  label?: string
  className?: string
}) {
  const active = useActiveSection(sections.map((section) => section.id))

  return (
    <nav
      aria-label={label}
      className={cn(
        'sticky top-16 z-30 border-y border-[var(--surface-border)] bg-[rgba(7,8,10,0.86)] backdrop-blur-xl',
        className,
      )}
    >
      <ul className="mx-auto flex max-w-[1200px] gap-1 overflow-x-auto px-4 py-2 [scrollbar-width:none] sm:px-6">
        {sections.map((section) => {
          const current = active === section.id
          return (
            <li key={section.id} className="shrink-0">
              <a
                href={`#${section.id}`}
                aria-current={current ? 'location' : undefined}
                className={cn(
                  'inline-flex min-h-9 items-center whitespace-nowrap rounded-full px-3.5 text-xs font-semibold no-underline transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent-readable)]',
                  current
                    ? 'bg-[var(--color-brand-accent)]/15 text-[var(--color-brand-accent)] ring-1 ring-inset ring-[var(--color-brand-accent)]/40'
                    : 'text-[var(--surface-muted)] hover:text-[var(--text-strong)]',
                )}
              >
                {section.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
